import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { Icons } from '@icons/icons';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { copyTextUtil } from '@utils/copy';

import { IAdaptiveTableCellProps } from './AdaptiveTableCell.types';

import { ESnackbarTypes } from '@enums/common';

import { DEFAULT_CELLS_VISIBLE_PART_MAX_WIDTH } from '@constants/common';

import DynamicIconWrapper from '../DynamicIconWrapper';
import Tooltip from '../Tooltip';

import './AdaptiveTableCell.scss';

const COPY_SNACKBAR_DELAY = 2000;

const AdaptiveTableCell = ({
	text,
	children,
	tooltipText,
	styleInline,
	className,
	onClick,
	onDoubleClick,
	actionHandler,
	iconAction,
	color,
	subText = '',
	maxTextWidth = DEFAULT_CELLS_VISIBLE_PART_MAX_WIDTH,
	isWithoutTooltip = false,
	isCopyable = false,
	isCentered = false,
}: IAdaptiveTableCellProps) => {
	const dispatch = useAppDispatch();

	const textRef = useRef<HTMLSpanElement | null>(null);
	const [maxWidth, setMaxWidth] = useState(maxTextWidth);

	const [isFullSize, setIsFullSize] = useState(false);

	const calculateFullSize = useCallback(() => {
		if (!text?.length) {
			setIsFullSize(true);
			return;
		}

		if (textRef?.current?.offsetWidth) {
			const fullSizeCol = textRef.current.offsetWidth;
			setIsFullSize(fullSizeCol < maxWidth);
		}
	}, [text?.length, maxWidth]);

	useLayoutEffect(() => {
		calculateFullSize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [text, textRef.current]);

	const calculateDynamicWidth = useCallback(() => {
		const clientWidth = textRef?.current?.offsetParent?.clientWidth || 0;
		const width = clientWidth < maxTextWidth
			? maxTextWidth
			: clientWidth;

		setMaxWidth(width);
	}, [maxTextWidth]);

	useLayoutEffect(() => {
		calculateDynamicWidth();
		window.addEventListener('resize', calculateDynamicWidth);

		return () => {
			window.removeEventListener('resize', calculateDynamicWidth);
		};
	}, [calculateDynamicWidth]);

	const copyHandler = () => {
		if (text) {
			copyTextUtil(text);

			dispatch(setSnackbar({
				message: 'Copied to clipboard',
				type: ESnackbarTypes.info,
				delay: COPY_SNACKBAR_DELAY,
			}));
		}
	};

	const onClickHandler = (e: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
		if (isCopyable) copyHandler();
		if (onClick) onClick(e);
	};

	return (
		<div className='f5gAdaptiveTableCellWrapper'>
			<div
				className={cn('f5gAdaptiveTableCellContainer', className, {
					'copy': isCopyable,
					'f5gAdaptiveTableCellCentered': isCentered,
				})}
				onClick={onClickHandler}
				onDoubleClick={onDoubleClick}
				style={styleInline}
				{...isCopyable && { title: 'Click to copy' }}
			>
				<Tooltip
					content={tooltipText || text}
					isDisabled={isFullSize || isWithoutTooltip}
					placement='top'
				>
					<span
						ref={textRef}
						className='f5gAdaptiveTableCellTextEllipsis'
						style={{
							display: !isFullSize ? 'block' : 'inline',
							maxWidth: `${maxWidth}px`,
						}}
					>
						{color && (
							<span
								className='f5gAdaptiveTableCellColor'
								style={{ background: color }}
							/>
						)}
						{text || children}
					</span>
				</Tooltip>
				{subText && (
					<span className='f5gAdaptiveTableCellSubText'>
						{subText}
					</span>
				)}
			</div>
			{actionHandler && (
				<DynamicIconWrapper
					icon={Icons.viewIcon || iconAction}
					onClick={actionHandler}
				/>
			)}
		</div>
	);
};

export default memo(AdaptiveTableCell);
